import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Archive, ArrowLeft, Plus } from 'lucide-react';
import {
  useOrganizations as useSessionOrganizations,
  useSetActiveOrganization,
} from '@repo/api-client/react';
import {
  useArchiveOrganization,
  useOrganization,
  useUpdateOrganization,
  useUploadOrgLogo,
} from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { BusinessAvatar, ImageUploader, brandingColor } from '@/features/avatar';
import { BusinessForm } from '@/features/org/BusinessForm';
import {
  businessFormFromOrganization,
  businessFormToUpdate,
} from '@/features/org/businessFormSchema';
import { useActiveRole } from '@/features/auth/useActiveRole';
import { TeamManagementPanel } from '@/features/team/TeamManagementPanel';

export function BusinessDetailScreen() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { organization, loading } = useOrganization(orgId);
  const { updateOrganization, loading: saving } = useUpdateOrganization();
  const { uploadLogo, loading: uploadingLogo } = useUploadOrgLogo();
  const { archiveOrganization, loading: archiving } = useArchiveOrganization();
  const setActive = useSetActiveOrganization();
  const orgsList = useSessionOrganizations();
  const { toast } = useToast();
  const { canEditOrg, canArchiveOrg } = useActiveRole();

  const [archiveOpen, setArchiveOpen] = useState(false);

  if (loading) {
    return (
      <Page>
        <Skeleton className="h-10 w-72" />
        <Skeleton className="mt-6 h-[60vh] rounded-xl" />
      </Page>
    );
  }

  if (!organization || !orgId) {
    return (
      <Page>
        <Alert variant="danger">Business not found.</Alert>
      </Page>
    );
  }

  async function onArchiveConfirm() {
    if (!organization) return;
    try {
      await archiveOrganization(organization.id);
      toast({ title: 'Business archived', variant: 'success' });
      const fallback = orgsList.find((o) => o.id !== organization.id);
      if (fallback) setActive(fallback.id);
      navigate('/settings/businesses');
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  async function onUploadLogo(file: File) {
    if (!organization) return;
    try {
      await uploadLogo({ organizationId: organization.id, file });
      toast({ title: 'Logo updated', variant: 'success' });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
      throw err;
    }
  }

  async function onRemoveLogo() {
    if (!organization) return;
    try {
      await updateOrganization({
        organizationId: organization.id,
        updates: { logoUrl: null },
      });
      toast({ title: 'Logo removed', variant: 'success' });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Page>
      <Button variant="link" size="sm" asChild className="-ml-2 mb-4">
        <Link to="/settings/businesses">
          <ArrowLeft className="size-4" /> Back to businesses
        </Link>
      </Button>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <BusinessAvatar
              id={organization.id}
              name={organization.name}
              initials={
                organization.nickname
                  ? organization.nickname.slice(0, 3).toUpperCase()
                  : undefined
              }
              color={brandingColor({
                id: organization.id,
                branding: organization.branding,
              })}
              logoUrl={organization.logoUrl ?? null}
              size={48}
            />
            <span className="truncate">{organization.nickname || organization.name}</span>
          </span>
        }
        description={organization.description ?? undefined}
        actions={
          canArchiveOrg ? (
            <Button variant="outline" onClick={() => setArchiveOpen(true)}>
              <Archive className="size-4" /> Archive
            </Button>
          ) : null
        }
      />

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
        </TabsList>
        <TabsContent value="profile" className="mt-6 flex flex-col gap-6">
          {canEditOrg ? (
          <Card>
            <CardContent className="pt-6">
              <ImageUploader
                title="Logo"
                hint="Shown on invoices and across the app. PNG or JPG, square works best, up to 5 MB."
                preview={
                  <BusinessAvatar
                    id={organization.id}
                    name={organization.name}
                    initials={
                      organization.nickname
                        ? organization.nickname.slice(0, 3).toUpperCase()
                        : undefined
                    }
                    color={brandingColor({
                      id: organization.id,
                      branding: organization.branding,
                    })}
                    logoUrl={organization.logoUrl ?? null}
                    size={72}
                    radius={16}
                    fontSize={22}
                  />
                }
                hasImage={Boolean(organization.logoUrl)}
                loading={uploadingLogo}
                onUpload={onUploadLogo}
                onRemove={onRemoveLogo}
              />
            </CardContent>
          </Card>
          ) : null}
          <fieldset disabled={!canEditOrg} className="disabled:opacity-60">
          <BusinessForm
            defaultValues={businessFormFromOrganization(organization)}
            loading={saving}
            submitLabel="Save changes"
            showSubmit={canEditOrg}
            onSubmit={async (values) => {
              if (!canEditOrg) return;
              try {
                await updateOrganization({
                  organizationId: organization.id,
                  updates: businessFormToUpdate(values),
                });
                toast({ title: 'Business saved', variant: 'success' });
              } catch (err) {
                const desc = describeAppError(err);
                toast({
                  title: desc.title,
                  description: desc.message,
                  variant: 'danger',
                });
              }
            }}
          />
          </fieldset>
        </TabsContent>
        <TabsContent value="team" className="mt-6">
          <TeamManagementPanel
            orgId={organization.id}
            orgName={organization.nickname || organization.name}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {organization.nickname || organization.name}?</DialogTitle>
            <DialogDescription>
              Archived businesses are hidden from the switcher and queued for permanent
              deletion in 30 days. You can restore them in the meantime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={archiving} onClick={onArchiveConfirm}>
              <Plus className="size-4 rotate-45" /> Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
